import moment from 'moment';
import { RECORD_STATUS } from '../../config';

const { appointment_planned } = RECORD_STATUS;

function getRecordFormId(record) {
  return record.record?.formId;
}

// returns as 1 argument recordIds in 1 and not in 2, second argument present in 2 not in 1, third presents in both
function compareTwoRecordsArrays(recArray1, recArray2) {
  if (!recArray1 || !recArray2) return [recArray1, recArray2];
  const ids1 = recArray1.map((r) => r.id);
  const ids2 = recArray2.map((r) => r.id);
  return [
    ids1.filter((i1) => !ids2.includes(i1)),
    ids2.filter((i2) => !ids1.includes(i2)),
    ids2.filter((i2) => ids1.includes(i2)),
  ];
}

export class ManageRecords {
  constructor({ formManager, recordHandler, recordRepo }) {
    this.formManager = formManager;
    this.recordHandler = recordHandler;
    this.recordRepo = recordRepo;
  }

  async getRecord(recordId) {
    let record = await this.getRecordFromRepo(recordId);
    if (!record) record = await this.recordHandler.get(recordId);
    return record;
  }

  getRecordFromApi(recordId) {
    return this.recordHandler.get(recordId);
  }

  getRecordFromRepo(recordId) {
    return this.recordRepo.get(recordId);
  }

  async saveRecordToRepo(recordId) {
    const record = await this.recordHandler.get(recordId);
    await this.recordRepo.put(recordId, record);
    const formId = record.record?.formId;
    if (formId && formId !== 0) await this.formManager.updateFormIfUpdated(formId);
  }

  async saveForm(formId) {
    await this.formManager.saveFormInLocal(formId);
  }

  async removeRecordFromRepo(recordId) {
    await this.recordRepo.delete(recordId);
  }

  async clearRecordDb() {
    await this.recordRepo.clear();
  }

  async getRecordFromApiIfNewer(recordId) {
    if (await this.shouldUpdateRecord(recordId)) {
      await this.saveRecordToRepo(recordId);
    }
  }

  async getRepoUpdatedAt(recordId) {
    return this.recordRepo.getUpdateDate(recordId);
  }

  async persistRecordsWithInspectionInProgress() {
    const records = await this.getRecordsWithInspectionInProgress();
    const localRecords = await this.recordRepo.all();
    const [localsToDelete, newDistantRecords, localAndDistant] = compareTwoRecordsArrays(localRecords, records);
    const localRecordsToUpdate = await this.compareUpdateDates(localAndDistant);
    const retrieveRecords = Promise.all([
      [...newDistantRecords, ...localRecordsToUpdate].map((id) => this.saveRecordToRepo(id)),
    ]);
    const deleteRecords = Promise.all([localsToDelete.map((id) => this.recordRepo.delete(id))]);

    await Promise.all([retrieveRecords, deleteRecords]);
  }

  // compare update date locally and distant and return
  async shouldUpdateRecord(recordId) {
    const localUpdateDate = await this.recordRepo.getUpdateDate(recordId);
    return this.remoteIsNewer(recordId, localUpdateDate);
  }

  async remoteIsNewer(recordId, lastUpdateAt) {
    const distantUpdateDate = await this.recordHandler.getUpdateDate(recordId);
    return !lastUpdateAt || moment(distantUpdateDate).isAfter(moment(lastUpdateAt));
  }

  async removeRecordDataFromRepo(recordId) {
    const record = await this.getRecordFromRepo(recordId);
    const formId = getRecordFormId(record);
    await this.formManager.removeFormDataFromRepo(formId);
    await this.removeRecordFromRepo(recordId);
  }

  async search({
    userId,
    status,
    refHexaId,
    concessionId,
    appointmentDate,
    updatedAtMin,
    range,
    mode,
    creationDateMax,
    creationDateMin,
    entryStockDateMax,
    entryStockDateMin,
    registration,
    vin,
    registrationDate,
    clic2sell,
    clic2buy,
    sourceIds,
    groupId,
    verifAccess,
    recordTypeId,
    guaranteedPrice,
    brandName,
    modelName,
    sort,
    sourceId,
    nrmStatus,
    nrmRecordId,
    publicPos,
    recordStatus,
    cancelCases,
    carcheck,
    useExpectedDeliveryDateForNrmAndNrmHbs,
    stellaClub,
    pushToCoverage,
    pushToStockSale,
  }) {
    const { records, totalCount } = await this.recordHandler.search({
      userId,
      status,
      refHexaId,
      concessionId,
      appointmentDate,
      updatedAtMin,
      range: range || [0, 49],
      mode: mode || 'full',
      creationDateMax,
      creationDateMin,
      entryStockDateMax,
      entryStockDateMin,
      registration,
      vin,
      registrationDate,
      clic2sell,
      clic2buy,
      sourceIds,
      groupId,
      verifAccess,
      recordTypeId,
      guaranteedPrice,
      brandName,
      modelName,
      sort,
      sourceId,
      nrmStatus,
      nrmRecordId,
      publicPos,
      recordStatus,
      cancelCases,
      carcheck,
      useExpectedDeliveryDateForNrmAndNrmHbs,
      stellaClub,
      pushToCoverage,
      pushToStockSale,
    });

    return { records, totalCount };
  }

  allInRepoIds() {
    return this.recordRepo.allIds();
  }

  async openRecord(recordId) {
    const record = await this.getRecord(recordId);
    const formId = getRecordFormId(record);
    await this.saveTempForm(formId);
    return record;
  }

  async saveTempForm(formId) {
    if (formId && !(await this.formManager.formInRepo(formId))) {
      await this.formManager.saveFormTempory(formId);
    }
  }

  async updateRecordStatus(recordId, status) {
    await this.recordHandler.updateStatus(recordId, status);
  }

  async updateBuyerExpertise(recordId, body) {
    await this.recordHandler.updateBuyerExpertise(recordId, body);
  }

  launchInspection(recordId, profile) {
    return this.recordHandler.pushToDm(recordId, profile);
  }

  createRecord(data) {
    const record = { ...data };
    record.status = appointment_planned;
    if (!Object.prototype.hasOwnProperty.call(record, 'origin')) record.origin = 'carcheck';
    try {
      record.refId = parseInt(record.refHexaId, 16);
    } catch (e) {
      record.refId = null;
    }
    return this.recordHandler.postRecord(record);
  }

  async updateAppointment(recordId, { appointmentDateTime, concessionId }) {
    await this.recordHandler.updateAppointment(recordId, { appointmentDateTime, concessionId });
  }

  async searchSupervisorCoverageRequest({
    userId,
    status,
    refHexaId,
    concessionId,
    appointmentDate,
    updatedAtMin,
    range,
    mode,
    creationDateMax,
    creationDateMin,
    registration,
    registrationDate,
    hbs,
    sourceId,
    groupId,
    verifAccess,
    recordTypeId,
    guaranteedPrice,
    brandName,
    modelName,
    sort,
    coverageDate,
    sale_offer_type,
  }) {
    const { records, totalCount } = await this.recordHandler.searchSupervisorCoverageRequest({
      userId,
      status,
      refHexaId,
      concessionId,
      appointmentDate,
      updatedAtMin,
      range: range || [0, 49],
      mode: mode || 'full',
      creationDateMax,
      creationDateMin,
      registration,
      registrationDate,
      hbs,
      sourceId,
      groupId,
      verifAccess,
      recordTypeId,
      guaranteedPrice,
      brandName,
      modelName,
      sort,
      coverageDate,
      sale_offer_type,
    });
    return { records, totalCount };
  }

  async getUserFeeds() {
    const res = await this.recordHandler.getUserFeeds();
    return res;
  }
}
